package org.example.project.enginemodule

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.example.project.dashboard.DashboardTheme
import org.example.project.settings.UISettings
import org.example.project.settings.LumaticaButton
import org.example.project.service.LizenzService
import org.example.project.service.RollenService
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private enum class StoreTab {
    SHOP,
    MINE
}

object ModulShopDashboardNavState {
    const val SHOP = "shop"
    const val MINE = "mine"

    var selectedTab by mutableStateOf(SHOP)
    var detailOpen by mutableStateOf(false)
    var detailResetKey by mutableIntStateOf(0)
    var requestedDetailModuleKey by mutableStateOf<String?>(null)
    var detailOpenRequestKey by mutableIntStateOf(0)

    fun openShop(syncToHash: Boolean = false) {
        detailOpen = false
        detailResetKey += 1
        selectedTab = SHOP
        if (syncToHash) window.location.hash = "#/store"
    }

    fun openMine(syncToHash: Boolean = false) {
        detailOpen = false
        detailResetKey += 1
        selectedTab = MINE
        if (syncToHash) window.location.hash = "#/store/mine"
    }

    fun openMineModule(moduleKey: String) {
        requestedDetailModuleKey = moduleKey
        detailOpenRequestKey += 1
        selectedTab = MINE
        detailOpen = true
    }
}

@Composable
fun ModulShopLayout(
    syncToHash: Boolean = true,
    readOnly: Boolean = false,
    onClose: (() -> Unit)? = null
) {
    Style(StoreStyles)

    if (syncToHash) {
        var selectedTab by remember {
            mutableStateOf(
                if (window.location.hash.contains("/store/mine")) StoreTab.MINE else StoreTab.SHOP
            )
        }
        var detailOpen by remember { mutableStateOf(false) }
        var detailResetKey by remember { mutableIntStateOf(0) }

        fun setTab(tab: StoreTab) {
            detailOpen = false
            detailResetKey += 1
            selectedTab = tab
            window.location.hash = if (tab == StoreTab.MINE) "#/store/mine" else "#/store"
        }

        Div({ classes(StoreStyles.page) }) {
            Main({ classes(StoreStyles.mainContent) }) {
                ModulShop(
                    viewMode = if (selectedTab == StoreTab.SHOP) ModulShopViewMode.SHOP else ModulShopViewMode.MINE,
                    readOnly = readOnly,
                    showHeaderTabs = false,
                    showFeaturedHero = selectedTab == StoreTab.SHOP && !detailOpen,
                    detailResetKey = detailResetKey,
                    onDetailVisibilityChange = { visible -> detailOpen = visible },
                    sideNavigationContent = {
                        Div({ classes(StoreStyles.navRail) }) {
                            StoreTabButton(
                                label = "Shop",
                                active = selectedTab == StoreTab.SHOP && !detailOpen,
                                onClick = { setTab(StoreTab.SHOP) }
                            )
                            StoreTabButton(
                                label = "Meine Inhalte",
                                active = selectedTab == StoreTab.MINE && !detailOpen,
                                onClick = { setTab(StoreTab.MINE) }
                            )
                        }
                    }
                )
            }
        }
        return
    }

    val effectiveScreens = LizenzService.getEffectiveScreens()
    val canShop = readOnly || ("Modul Shop" in effectiveScreens && RollenService.hasReadPermission("Modul Shop"))
    val canMine = !readOnly && "MeineModule" in effectiveScreens && RollenService.hasReadPermission("MeineModule")
    if (!canShop && !canMine) return
    val showMine = canMine && (!canShop || ModulShopDashboardNavState.selectedTab == ModulShopDashboardNavState.MINE)

    Div({ classes(StoreStyles.dashboardPage) }) {
        Main({ classes(StoreStyles.dashboardMainContent) }) {
            ModulShop(
                viewMode = if (showMine) ModulShopViewMode.MINE else ModulShopViewMode.SHOP,
                readOnly = readOnly,
                showHeaderTabs = false,
                showFeaturedHero = !ModulShopDashboardNavState.detailOpen,
                heroNavigationContent = if (canShop && canMine) ({
                    LumaticaButton(
                        label = if (showMine) "Modul Shop" else "Meine Module",
                        onClick = {
                            if (showMine) ModulShopDashboardNavState.openShop()
                            else ModulShopDashboardNavState.openMine()
                        }
                    )
                }) else null,
                detailResetKey = ModulShopDashboardNavState.detailResetKey,
                requestedDetailModuleKey = ModulShopDashboardNavState.requestedDetailModuleKey,
                detailOpenRequestKey = ModulShopDashboardNavState.detailOpenRequestKey,
                onDetailVisibilityChange = { visible -> ModulShopDashboardNavState.detailOpen = visible }
            )
        }
    }
}

@Composable
private fun StoreTabButton(
    label: String,
    active: Boolean,
    onClick: () -> Unit
) {
    Span({
        classes(StoreStyles.tabButton)
        if (active) classes(StoreStyles.tabButtonActive)
        onClick { onClick() }
    }) { Text(label) }
}

private object StoreStyles : StyleSheet() {
    val page by style {
        minHeight(100.vh)
        backgroundColor(DashboardTheme.BACKGROUND_APP)
        property("background", DashboardTheme.BACKGROUND_APP_GRADIENT)
        color(DashboardTheme.TEXT_LIGHT)
    }

    val dashboardPage by style {
        width(100.percent)
        minHeight(100.percent)
        backgroundColor(Color.transparent)
        color(DashboardTheme.TEXT_LIGHT)
    }

    val navRail by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(30.px)
    }

    val tabButton by style {
        color(Color("rgba(255,255,255,0.72)"))
        fontSize(17.px)
        fontWeight("500")
        cursor("pointer")
        padding(10.px, 2.px)
        property("border-bottom", "2px solid transparent")
        property("transition", "color 0.18s ease, border-color 0.18s ease")
        hover { color(Color.white) }
    }

    val tabButtonActive by style {
        property("border-bottom", "2px solid ${UISettings.ACCENT}")
    }

    val mainContent by style {
        minHeight(100.vh)
        boxSizing("border-box")
    }

    val dashboardMainContent by style {
        minHeight(100.percent)
        boxSizing("border-box")
    }
}
